import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MusicShell from "../fanHome/MusicShell";
import { ACCENT } from "../artistStudio/shared";
import { useErrorToast } from "../common/errorToast";
import { licenceNoticeApi } from "./licenceNoticeApi";

// /rights/licences-needed: for a Creator or Business account, the videos it published that use a sound it needs a licence for,
// grouped by sound, each with a "Get a licence" link to the sound page. This is a list only: nothing is muted, disabled or removed.
// A Personal account always gets an empty list. The same list reaches the user as an in-app notice and an email when the account
// is reclassified.
const videoLabel = (video) => video.title || `Video ${video.post_id}`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export default function LicencesNeeded() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");
  useErrorToast(error);

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      setData(await licenceNoticeApi.needed());
      setStatus("ready");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Could not load your licences list.");
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sounds = (data && data.sounds) || [];
  const personal = data && data.account_class === "personal";

  return (
    <MusicShell maxWidth="max-w-3xl">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Licences you need</h1>
      <p className="mt-1 text-gray-600">Videos you posted that use a sound which needs a licence for your type of account. Nothing is muted or removed; get a licence to keep using the sound.</p>
      {status === "loading" && <p className="mt-6 text-gray-500" role="status">Loading…</p>}
      {status === "error" && (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700" role="alert">
          We could not load this list. <button type="button" onClick={load} className="font-semibold underline">Try again</button>
        </div>
      )}
      {status === "ready" && (
        <>
          <p className="mt-5 text-sm text-gray-600" data-testid="licences-needed-class">Your account type: <span className="font-semibold text-gray-900">{data.class_label}</span></p>
          {sounds.length === 0 && (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-6 text-center text-gray-600" data-testid="licences-needed-empty">
              {personal
                ? "You have a Personal account, so none of your videos need a licence."
                : "None of your videos need a licence right now. If that changes, the details will appear here."}
            </div>
          )}
          {sounds.length > 0 && (
            <>
              <p className="mt-2 text-sm font-semibold text-gray-900" data-testid="licences-needed-total">{plural(data.total_videos, "video")} on {plural(sounds.length, "sound")}</p>
              <ul className="mt-4 space-y-3" data-testid="licences-needed-list">
                {sounds.map((s) => (
                  <li key={s.track_id} className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="block font-semibold text-gray-900">{s.title}</span>
                        <span className="text-sm text-gray-500">{plural(s.videos.length, "video")}</span>
                      </div>
                      <Link
                        to={`/sound/${s.track_id}`}
                        className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                        style={{ backgroundColor: ACCENT }}
                      >
                        Get a licence
                      </Link>
                    </div>
                    <ul className="mt-2 space-y-1 text-sm text-gray-600">
                      {s.videos.map((v) => (
                        <li key={v.post_id}><Link to={`/post/${v.post_id}`} className="hover:underline">{videoLabel(v)}</Link></li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </MusicShell>
  );
}
